import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import loadable from '@loadable/component';

import LoginAssistantWrapper from './LoginAssistantWrapper';

import { setFederatedLoginModalVisibility, setHeaderFooterVisibility } from 'actions/headerfooter';
import { SEARCH_BEST_FOR_YOU_COOKIE } from 'constants/cookies';
import { HYDRA_PRIME_TWO_DAY_SHIPPING, HYRDA_MINIFIED_MOBILE_HEADER } from 'constants/hydraTests';
import { CHECKOUT_URL_RE, CONFIRMATION_URL_RE } from 'common/regex';
import { isAssigned, triggerAssignment } from 'actions/ab';
import { setError } from 'actions/errors';
import Header from 'hf/Header';
import CondensedHeader from 'hf/CondensedHeader';
import Footer from 'hf/Footer';
import CartModal from 'components/cart/CartModal';
import { componentDidCatchFullPageError } from 'common/componentHooks';
import ErrorPage from 'components/error/ErrorPage';
import NotificationsOverlay from 'components/NotificationsOverlay';
import PixelServer from 'containers/pixelserver/PixelServer';
import { ensureClass, ensureNoClass } from 'helpers';
import { toggleNotificationsOverlay } from 'actions/notifications';
import { updateBestForYou } from 'actions/search';
import { redirectTo } from 'actions/redirect';
import { setLastPageWasOrderconfirmation } from 'store/ducks/history/actions';

const FederatedLoginModal = loadable(() => import('components/account/FederatedLoginModal'));

// order matters
import 'styles/global.scss';
import styles from 'styles/App.scss';

export class App extends Component {

  static fetchDataOnServer(store) {
    const { cookies, routing: { locationBeforeTransitions: location } } = store.getState();
    store.dispatch(updateBestForYou(!cookies[SEARCH_BEST_FOR_YOU_COOKIE] || cookies[SEARCH_BEST_FOR_YOU_COOKIE] !== 'active'));
    if (location.query?.disableHf === 'true') {
      store.dispatch(setHeaderFooterVisibility(false));
    }
  }

  componentDidUpdate(prevProps) {
    const { location: { pathname: prevPathname, query: prevQuery }, wasLastPageOrderConfirmation, setLastPageWasOrderconfirmation, error: prevError } = prevProps;
    const { isFacetsVisible, error, location: { query }, setHeaderFooterVisibility } = this.props;
    const resetLastPageOrderConfirmation = wasLastPageOrderConfirmation && !(CONFIRMATION_URL_RE.test(prevPathname) || CHECKOUT_URL_RE.test(prevPathname));

    if (resetLastPageOrderConfirmation) {
      setLastPageWasOrderconfirmation(false);
    }

    if (prevQuery?.disableHf !== query?.disableHf && query?.disableHf === 'true') {
      setHeaderFooterVisibility(false);
    }

    // When client routing related to search facets, maintain facet state (ie. keep facets open if needed).
    // Also ensures facet state is maintained while swapping between Wildcard/Search containers.
    if (isFacetsVisible) {
      ensureClass(document.body, styles.activeSidebar);
      ensureNoClass(document.body, 'activeMain');
    } else {
      ensureClass(document.body, 'activeMain');
      ensureNoClass(document.body, styles.activeSidebar);
    }

    // Scroll to top on client-side error
    if (!prevError && error) {
      window.scrollTo(0, 0);
    }
  }

  componentDidCatch(error, info) {
    componentDidCatchFullPageError(this.props.setError, error, info);
  }

  makeChildren() {
    if (this.props.error) {
      return <ErrorPage error={this.props.error} />;
    } else {
      return React.Children.map(this.props.children, child => React.cloneElement(child, { setLayoutOption: this.setLayoutOption }));
    }
  }

  setLayoutOption = (option, value = true) => {
    const newState = {};
    newState[option] = value;
    this.setState(newState);
  };

  dismissOverlay = () => {
    this.props.toggleNotificationsOverlay();
  };

  handleCloseFederatedLogin = () => {
    const { federatedLoginModal: { redirectOnClose }, setFederatedLoginModalVisibility, redirectTo } = this.props;
    setFederatedLoginModalVisibility(false);
    !!redirectOnClose && redirectTo(redirectOnClose);
  };

  render() {
    const {
      content,
      federatedLoginModal,
      hydraCondensedHeader,
      hydraCondensedHeaderSearchHidden,
      isAssignedPrimeTwoDayShipping,
      isModalShowing,
      isHfVisible,
      notifications: { showOverlay },
      location
    } = this.props;

    const { isFederatedLoginModalShowing, returnTo } = federatedLoginModal;
    const { global: federatedLoginModalCopy = {} } = content;
    return (
      <div>
        { isModalShowing && <CartModal location={location}/> }
        {isHfVisible
          ? (hydraCondensedHeader || hydraCondensedHeaderSearchHidden)
            ? <CondensedHeader hydraCondensedHeader={hydraCondensedHeader} hydraCondensedHeaderSearchHidden={hydraCondensedHeaderSearchHidden} />
            : <Header />
          : null
        }
        <div key="main" className={styles.pageLayout}>
          <main id="main" className={styles.mainContent}>
            <div>
              {this.makeChildren()}
            </div>
          </main>
        </div>
        <PixelServer />
        {<NotificationsOverlay showOverlay={showOverlay} dismissOverlay={this.dismissOverlay} />}
        {isHfVisible &&
          <>
            <LoginAssistantWrapper />
            <Footer />
          </>
        }
        {isFederatedLoginModalShowing &&
          <FederatedLoginModal
            copy={federatedLoginModalCopy}
            handleClose={this.handleCloseFederatedLogin}
            hydraPrimeTwoDayShipping={isAssignedPrimeTwoDayShipping}
            isOpen={isFederatedLoginModalShowing}
            returnTo={returnTo}
          />
        }
      </div>
    );
  }
}

App.contextTypes = {
  router: PropTypes.object.isRequired
};

App.propTypes = {
  // Injected by React Redux
  error: PropTypes.object,
  // Injected by React Router
  children: PropTypes.node
};

function mapStateToProps(state) {
  const {
    error,
    isFacetsVisible,
    headerFooter: { content, federatedLoginModal, isHfVisible },
    products,
    notifications,
    cart: { isModalShowing },
    routing: { locationBeforeTransitions: location },
    history: { wasLastPageOrderConfirmation },
    rewards
  } = state;

  return {
    content,
    error,
    federatedLoginModal,
    isFacetsVisible,
    isHfVisible,
    hydraCondensedHeader: isAssigned(HYRDA_MINIFIED_MOBILE_HEADER, 1, state),
    hydraCondensedHeaderSearchHidden: isAssigned(HYRDA_MINIFIED_MOBILE_HEADER, 2, state),
    isAssignedPrimeTwoDayShipping: isAssigned(HYDRA_PRIME_TWO_DAY_SHIPPING, 1, state),
    products,
    notifications,
    isModalShowing,
    location,
    rewards,
    wasLastPageOrderConfirmation
  };
}

export default connect(mapStateToProps, {
  redirectTo,
  setError,
  setHeaderFooterVisibility,
  setFederatedLoginModalVisibility,
  setLastPageWasOrderconfirmation,
  toggleNotificationsOverlay,
  triggerAssignment
})(App);
